import { useState, useCallback, useEffect, useMemo, createContext, useContext, ReactNode } from 'react'
import { nanoid } from 'nanoid'
import { useTranslation } from '../contexts/LanguageContext'
import type { CV } from '../types/cv'
import type { CVDocument } from '../services/firebase/firestore'
import type { TemplateType } from './usePdfGenerator'

const LOCAL_CVS_KEY = 'cvtoday_local_cvs'

export interface LocalCVDocument {
  id: string
  title: string
  cv: CV
  template: TemplateType
  createdAt: string // ISO string for JSON serialization
  updatedAt: string
  isDefault: boolean
}

function getLocalCVs(): LocalCVDocument[] {
  if (typeof window === 'undefined') return []
  try {
    const stored = localStorage.getItem(LOCAL_CVS_KEY)
    return stored ? JSON.parse(stored) : []
  } catch {
    return []
  }
}

function saveLocalCVs(cvs: LocalCVDocument[]): void {
  if (typeof window === 'undefined') return
  localStorage.setItem(LOCAL_CVS_KEY, JSON.stringify(cvs))
}

// Convert LocalCVDocument to CVDocument format
function toFirestoreFormat(local: LocalCVDocument): CVDocument {
  return {
    ...local,
    createdAt: new Date(local.createdAt),
    updatedAt: new Date(local.updatedAt),
  }
}

interface LocalCVListContextValue {
  cvs: CVDocument[]
  loading: boolean
  addCV: (cv: CV, title?: string, template?: TemplateType) => string
  updateCV: (id: string, data: Partial<{ title: string; cv: CV; template: TemplateType }>) => void
  deleteCV: (id: string) => void
  setDefault: (id: string) => void
  getCV: (id: string) => CVDocument | null
  refresh: () => void
}

const LocalCVListContext = createContext<LocalCVListContextValue | null>(null)

export function LocalCVListProvider({ children }: { children: ReactNode }) {
  const { t } = useTranslation()
  const [cvs, setCVs] = useState<LocalCVDocument[]>(() => getLocalCVs())
  const [loading] = useState(false)

  // Listen for storage changes from other tabs/windows
  useEffect(() => {
    const handleStorage = (e: StorageEvent) => {
      if (e.key === LOCAL_CVS_KEY) {
        setCVs(getLocalCVs())
      }
    }
    window.addEventListener('storage', handleStorage)
    return () => window.removeEventListener('storage', handleStorage)
  }, [])

  const refresh = useCallback(() => {
    setCVs(getLocalCVs())
  }, [])

  const addCV = useCallback((cv: CV, title?: string, template: TemplateType = 'clean'): string => {
    const id = nanoid()
    const now = new Date().toISOString()
    const newCV: LocalCVDocument = {
      id,
      title: title || cv.personalInfo?.fullName || t.cvList.newCVDefault,
      cv,
      template,
      createdAt: now,
      updatedAt: now,
      isDefault: false,
    }

    setCVs((prev) => {
      const updated = [newCV, ...prev]
      saveLocalCVs(updated)
      return updated
    })

    return id
  }, [t])

  const updateCV = useCallback((id: string, data: Partial<{ title: string; cv: CV; template: TemplateType }>) => {
    setCVs((prev) => {
      const updated = prev.map((item) =>
        item.id === id
          ? { ...item, ...data, updatedAt: new Date().toISOString() }
          : item
      )
      saveLocalCVs(updated)
      return updated
    })
  }, [])

  const deleteCV = useCallback((id: string) => {
    setCVs((prev) => {
      const updated = prev.filter((item) => item.id !== id)
      saveLocalCVs(updated)
      return updated
    })
  }, [])

  const setDefault = useCallback((id: string) => {
    setCVs((prev) => {
      const updated = prev.map((item) => ({
        ...item,
        isDefault: item.id === id,
        updatedAt: item.id === id ? new Date().toISOString() : item.updatedAt,
      }))
      saveLocalCVs(updated)
      return updated
    })
  }, [])

  const getCV = useCallback((id: string): CVDocument | null => {
    const found = cvs.find((cv) => cv.id === id)
    return found ? toFirestoreFormat(found) : null
  }, [cvs])

  // Memoize the transformed cvs array to prevent unnecessary re-renders
  const transformedCVs = useMemo(() => cvs.map(toFirestoreFormat), [cvs])

  // Memoize the context value to ensure proper update propagation
  const value: LocalCVListContextValue = useMemo(() => ({
    cvs: transformedCVs,
    loading,
    addCV,
    updateCV,
    deleteCV,
    setDefault,
    getCV,
    refresh,
  }), [transformedCVs, loading, addCV, updateCV, deleteCV, setDefault, getCV, refresh])

  return (
    <LocalCVListContext.Provider value={value}>
      {children}
    </LocalCVListContext.Provider>
  )
}

export function useLocalCVList(): LocalCVListContextValue {
  const context = useContext(LocalCVListContext)
  if (!context) {
    throw new Error('useLocalCVList must be used within a LocalCVListProvider')
  }
  return context
}

// Helper to check if there are any local CVs
export function hasLocalCVs(): boolean {
  return getLocalCVs().length > 0
}

// Helper to get count of local CVs
export function getLocalCVCount(): number {
  return getLocalCVs().length
}
